<script setup>
import { nextTick, ref, watch } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import AiAPI from 'custom/api/ai';
import { useLocoI18n } from 'custom/composables/useLocoI18n';
import { useConversationAi } from './useConversationAi';
import { safeUrl } from './helpers';

// Copilot: ask about the open conversation and the inbox assistant's knowledge ("what did they order?", "what's
// our refund policy?"). Answers can go into the reply box. Nothing is sent to the customer.
const props = defineProps({
  conversationId: { type: Number, required: true },
});
const emit = defineEmits(['close']);

const { t } = useLocoI18n();
const { refresh } = useConversationAi();

const SUGGESTIONS = ['SUMMARY', 'WANTS', 'NEXT'];

const turns = ref([]);
const draft = ref('');
const isThinking = ref(false);
const listRef = ref(null);

const scrollDown = async () => {
  await nextTick();
  listRef.value?.scrollTo({ top: listRef.value.scrollHeight });
};

const ask = async question => {
  const content = question.trim();
  if (!content || isThinking.value) return;
  const history = turns.value
    .filter(turn => !turn.error)
    .map(turn => ({ role: turn.role, content: turn.content }));
  turns.value.push({ role: 'user', content });
  draft.value = '';
  isThinking.value = true;
  scrollDown();
  try {
    const { data } = await AiAPI.copilot(
      props.conversationId,
      content,
      history
    );
    turns.value.push({
      role: 'assistant',
      content: data.answer,
      sources: data.sources,
    });
    refresh(props.conversationId); // the conversation's AI cost changed
  } catch (error) {
    turns.value.push({
      role: 'assistant',
      error: true,
      content: error.response?.data?.error || error.message,
    });
  } finally {
    isThinking.value = false;
    scrollDown();
  }
};

const useInReply = content => {
  emitter.emit(BUS_EVENTS.INSERT_INTO_RICH_EDITOR, content);
};

watch(
  () => props.conversationId,
  () => {
    turns.value = [];
  }
);
</script>

<template>
  <aside
    class="fixed z-50 top-0 bottom-0 end-0 w-full sm:w-96 flex flex-col bg-n-solid-1 shadow-xl border-s border-n-weak"
  >
    <header
      class="flex items-center gap-2 px-4 h-14 border-b border-n-weak flex-shrink-0"
    >
      <Icon icon="i-lucide-sparkles" class="size-4 text-n-iris-11" />
      <span class="font-medium text-n-slate-12 flex-1">
        {{ t('LOCO.AI.COPILOT.TITLE') }}
      </span>
      <Button
        xs
        ghost
        slate
        icon="i-lucide-x"
        :title="t('LOCO.AI.COPILOT.CLOSE')"
        @click="emit('close')"
      />
    </header>

    <div ref="listRef" class="flex-1 overflow-y-auto p-4 flex flex-col gap-3">
      <div v-if="!turns.length" class="flex flex-col gap-3">
        <p class="text-sm text-n-slate-11">{{ t('LOCO.AI.COPILOT.INTRO') }}</p>
        <button
          v-for="suggestion in SUGGESTIONS"
          :key="suggestion"
          type="button"
          class="self-start px-3 py-1.5 rounded-full text-sm text-start text-n-slate-12 outline outline-1 -outline-offset-1 outline-n-weak hover:bg-n-alpha-1"
          @click="ask(t(`LOCO.AI.COPILOT.SUGGESTIONS.${suggestion}`))"
        >
          {{ t(`LOCO.AI.COPILOT.SUGGESTIONS.${suggestion}`) }}
        </button>
      </div>

      <div
        v-for="(turn, index) in turns"
        :key="index"
        class="flex flex-col gap-1"
        :class="turn.role === 'user' ? 'items-end' : 'items-start'"
      >
        <div
          class="max-w-[90%] px-3 py-2 rounded-xl text-sm whitespace-pre-wrap break-words"
          :class="{
            'bg-n-brand text-white': turn.role === 'user',
            'bg-n-alpha-2 text-n-slate-12':
              turn.role === 'assistant' && !turn.error,
            'bg-n-ruby-3 text-n-ruby-11': turn.error,
          }"
        >
          {{ turn.content }}
        </div>
        <template v-if="turn.role === 'assistant' && !turn.error">
          <div
            v-if="turn.sources?.length"
            class="flex flex-wrap gap-1 text-xs text-n-slate-11"
          >
            <component
              :is="source.url ? 'a' : 'span'"
              v-for="source in turn.sources"
              :key="source.source_id"
              :href="safeUrl(source.url)"
              target="_blank"
              rel="noopener noreferrer"
              class="px-2 py-0.5 rounded-full bg-n-alpha-1 truncate max-w-full"
              :class="{ 'hover:text-n-blue-11': source.url }"
            >
              {{ source.title || source.url }}
            </component>
          </div>
          <Button
            xs
            link
            icon="i-lucide-corner-down-left"
            :label="t('LOCO.AI.COPILOT.USE')"
            @click="useInReply(turn.content)"
          />
        </template>
      </div>

      <div
        v-if="isThinking"
        class="flex items-center gap-2 text-sm text-n-slate-11"
      >
        <Icon icon="i-lucide-loader-circle" class="size-4 animate-spin" />
        {{ t('LOCO.AI.COPILOT.THINKING') }}
      </div>
    </div>

    <form
      class="flex gap-2 p-3 border-t border-n-weak flex-shrink-0"
      @submit.prevent="ask(draft)"
    >
      <input
        v-model="draft"
        type="text"
        class="!mb-0 flex-1"
        :placeholder="t('LOCO.AI.COPILOT.PLACEHOLDER')"
        :disabled="isThinking"
      />
      <Button
        type="submit"
        icon="i-lucide-send-horizontal"
        :disabled="!draft.trim() || isThinking"
      />
    </form>
  </aside>
</template>
